import { useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusChip } from './JurisdictionBadge';
import { CHECKLIST_ROWS, INFORMATIONAL_NOTICE, SUI_EXPLANATION, SUI_OVERRIDE_NOTE, type ChecklistRowDefinition } from './checklistContent';
import { EFFECT_LABEL, PARTY_LABEL, countsSentence, worstStatus } from './statusLabels';
import { TAX_TERM_HELP, TaxTermHelp } from './TaxTermHelp';
import type { RequirementStatus, TaxEffect, TaxSetupRequirement, TaxSetupStatus, TaxSetupView } from './types';

interface RowModel {
  def: ChecklistRowDefinition;
  requirements: TaxSetupRequirement[];
  status: RequirementStatus;
  summary: string;
  /** Nothing to ask or explain: the requirement is not applicable here, or EPN has no form for this jurisdiction. The row shows only that. */
  onlyMessage: boolean;
}

/** Splits the requirements into the seven rows. A requirement with an unknown section goes to the last row, so nothing the server says is hidden. */
function buildRows(status: TaxSetupStatus): RowModel[] {
  const assigned = new Set<TaxSetupRequirement>();
  const rows: RowModel[] = CHECKLIST_ROWS.map((def) => {
    const requirements = status.requirements.filter((r) => {
      const belongs = def.sections.includes(r.section) || (def.key === 'unemployment' && r.key === 'employerSetup');
      if (belongs) assigned.add(r);
      return belongs;
    });
    return { def, requirements, status: 'NeedsReview', summary: '', onlyMessage: false };
  });
  const last = rows[rows.length - 1]!;
  last.requirements = status.requirements.filter((r) => !assigned.has(r));

  for (const row of rows) {
    if (row.def.key === 'status') {
      row.status = status.status;
      row.summary = `${countsSentence(status.counts)}. Informational only: payroll tax calculation is not yet certified.`;
      continue;
    }
    if (row.requirements.length === 0) {
      row.status = 'NeedsReview';
      row.summary = 'EPN has not evaluated this requirement yet. A reviewer will check it.';
      continue;
    }
    row.status = worstStatus(row.requirements);
    const lead = row.requirements.find((r) => r.status === row.status) ?? row.requirements[0]!;
    row.summary = lead.message;
    const election = row.requirements.find((r) => r.key === 'election');
    row.onlyMessage = row.status === 'NotApplicable' || (row.def.key === 'stateWithholding' && election?.status === 'Unsupported');
  }
  return rows;
}

function affectsOf(requirements: TaxSetupRequirement[]): TaxEffect[] {
  const set = new Set(requirements.filter((r) => r.status !== 'NotApplicable').flatMap((r) => r.affects));
  return (['Calculation', 'Payment', 'Filing'] as const).filter((e) => set.has(e));
}

/** A place the reader can go to supply a missing fact: a link, so it can be opened in a new tab and read by a screen reader as a link. Offered only by the screen that knows where the fact is edited. */
export interface RequirementAction {
  label: string;
  to: string;
}

/** Actions by requirement key (`workState`, `residenceState`). Only a requirement that still needs information gets its action. */
export type RequirementActions = Partial<Record<string, RequirementAction>>;

function RequirementItem({ requirement, action }: { requirement: TaxSetupRequirement; action?: RequirementAction | undefined }) {
  return (
    <li className="ts-req" data-testid={`requirement-${requirement.key}`}>
      <div className="ts-req__head">
        <span className="ts-req__title">{requirement.title}</span>
        <StatusChip status={requirement.status} />
        {requirement.blocksSetupCompletion && requirement.status !== 'Complete' && requirement.status !== 'NotApplicable' && (
          <span className="ts-tag">Blocks setup completion</span>
        )}
      </div>
      {requirement.message && <p className="ts-req__message">{requirement.message}</p>}
      {requirement.status !== 'Complete' && requirement.status !== 'NotApplicable' && (
        <p className="ts-req__meta">
          <span className="ts-req__label">Who acts:</span> {PARTY_LABEL[requirement.responsibleParty]}
          {requirement.nextAction && (
            <>
              {' '}
              <span className="ts-req__label">Next:</span> {requirement.nextAction}
            </>
          )}
        </p>
      )}
      {action && requirement.status === 'NeedsInformation' && (
        <p className="ts-req__action">
          <Link to={action.to} data-testid={`requirement-action-${requirement.key}`}>
            {action.label}
          </Link>
        </p>
      )}
    </li>
  );
}

function RowBody({ row, status, view, actions }: { row: RowModel; status: TaxSetupStatus; view: TaxSetupView; actions?: RequirementActions | undefined }) {
  const { def } = row;
  const g = def.guidance;
  const affects = affectsOf(row.requirements);
  if (def.key === 'status') {
    return (
      <div className="ts-row__body">
        <p className="ts-row__lead">
          Overall: <StatusChip status={status.status} />
        </p>
        <p>{INFORMATIONAL_NOTICE}</p>
        {status.warnings.length > 0 && (
          <ul className="ts-warnings" aria-label="Warnings">
            {status.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
        {row.requirements.length > 0 && (
          <ul className="ts-reqs">
            {row.requirements.map((r) => (
              <RequirementItem key={r.key} requirement={r} action={actions?.[r.key]} />
            ))}
          </ul>
        )}
        <p className="ts-note">Reference data version {status.referenceDataVersion || 'unknown'} ({status.referenceDataStatus}).</p>
      </div>
    );
  }
  return (
    <div className="ts-row__body">
      {row.requirements.length > 0 && (
        <ul className="ts-reqs">
          {row.requirements.map((r) => (
            <RequirementItem key={r.key} requirement={r} action={actions?.[r.key]} />
          ))}
        </ul>
      )}
      {def.key === 'unemployment' && (
        <div className="ts-sui" data-testid="sui-explanation">
          <p>{SUI_EXPLANATION}</p>
          <p>
            <strong>{SUI_OVERRIDE_NOTE}</strong>
            {view === 'employee' && ' Your payroll administrator and your employer manage company registrations.'}
          </p>
        </div>
      )}
      {!row.onlyMessage && (
        <dl className="ts-guide">
          <div>
            <dt>The question</dt>
            <dd>{g.question}</dd>
          </div>
          <div>
            <dt>Why EPN needs it</dt>
            <dd>{g.why}</dd>
          </div>
          <div>
            <dt>Where to find the answer</dt>
            <dd>{g.where}</dd>
          </div>
          <div>
            <dt>Does it come from an agency notice?</dt>
            <dd>{g.agencyNotice}</dd>
          </div>
          <div>
            <dt>Example format</dt>
            <dd>{g.example}</dd>
          </div>
          {affects.length > 0 && (
            <div>
              <dt>What it affects</dt>
              <dd>{affects.map((e) => EFFECT_LABEL[e]).join(', ')}</dd>
            </div>
          )}
        </dl>
      )}
      {!row.onlyMessage && g.terms.length > 0 && (
        <p className="ts-terms">
          <span className="ts-req__label">Terms:</span>{' '}
          {g.terms.map((t) => (
            <span key={t} className="ts-term">
              {TAX_TERM_HELP[t].term}
              <TaxTermHelp term={t} />
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/**
 * The guided tax setup checklist: seven rows, each with a status chip and a one-line summary that opens to the question, why EPN needs it, where to find
 * the answer, whether it comes from an agency notice, an example format and what it affects. It shows only what is relevant: a territory has no state
 * election fields and a state without a wage income tax is "Not applicable". Everything is derived from the server's status; nothing is invented here.
 */
export function TaxSetupChecklist({ status, view, actions }: { status: TaxSetupStatus; view: TaxSetupView; actions?: RequirementActions | undefined }) {
  const uid = useId();
  const rows = useMemo(() => buildRows(status), [status]);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  function toggle(key: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <ol className="ts ts-checklist" aria-label="Tax setup checklist" data-testid="tax-setup-checklist">
      {rows.map((row, i) => {
        const isOpen = open.has(row.def.key);
        const headId = `${uid}-h-${row.def.key}`;
        const panelId = `${uid}-p-${row.def.key}`;
        return (
          <li key={row.def.key} className="ts-row" data-testid={`checklist-row-${row.def.key}`}>
            <h4 className="ts-row__heading" id={headId}>
              <button type="button" className="ts-row__toggle" aria-expanded={isOpen} aria-controls={panelId} onClick={() => toggle(row.def.key)}>
                <span className="ts-row__num" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="ts-row__text">
                  <span className="ts-row__title">{row.def.title}</span>
                  <span className="ts-row__summary">{row.summary}</span>
                </span>
                <StatusChip status={row.status} />
                <span className="ts-row__caret" aria-hidden="true" />
              </button>
            </h4>
            {isOpen && (
              <div id={panelId} role="region" aria-labelledby={headId} className="ts-row__panel">
                <RowBody row={row} status={status} view={view} actions={actions} />
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
